import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Heart, X, ArrowLeft, MapPin, Briefcase, DollarSign,
  Building2, User, Star, Loader2, ChevronRight, RefreshCw,
  Brain, Zap
} from 'lucide-react';
import api from '../../utils/api';
import useAuthStore from '../../store/authStore';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:3001';

// ── Cartão de Vaga (para candidato) ───────────────────────────
function CardVaga({ vaga, onCurtir, onPular, loading }) {
  const score = vaga.score_total || 0;
  const corScore = score >= 70 ? 'text-green-600 bg-green-50' :
                   score >= 50 ? 'text-yellow-600 bg-yellow-50' :
                   'text-gray-500 bg-gray-50';
  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-sm">
      {/* Header com empresa */}
      <div className="bg-gradient-to-br from-acre-dark to-[#1a3820] p-6 text-white">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {vaga.logo_empresa
              ? <img src={`${API_BASE}${vaga.logo_empresa}`} alt={vaga.empresa} className="w-full h-full object-contain p-1"/>
              : <Building2 size={22} className="text-white/60"/>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-sora font-bold text-base truncate">{vaga.titulo}</p>
            <p className="text-white/60 text-sm truncate">{vaga.empresa}</p>
          </div>
          <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${corScore}`}>
            {score}%
          </span>
        </div>
      </div>

      {/* Detalhes */}
      <div className="p-5 space-y-3">
        <div className="flex flex-wrap gap-2">
          {vaga.cidade_vaga && (
            <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">
              <MapPin size={11}/>{vaga.cidade_vaga}, AC
            </span>
          )}
          {vaga.modalidade && (
            <span className="flex items-center gap-1 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full capitalize">
              <Briefcase size={11}/>{vaga.modalidade}
            </span>
          )}
          {vaga.salario_min && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2.5 py-1 rounded-full font-semibold">
              <DollarSign size={11}/>R$ {Number(vaga.salario_min).toLocaleString('pt-BR')}
            </span>
          )}
        </div>

        {/* Areas */}
        {vaga.areas_contratacao?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {vaga.areas_contratacao.slice(0,3).map(a => (
              <span key={a} className="text-xs bg-acre-light text-acre-green border border-acre-green/20 px-2 py-0.5 rounded-full font-medium">{a}</span>
            ))}
          </div>
        )}

        {/* Score breakdown */}
        <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
          <p className="text-xs font-semibold text-gray-500 mb-2">Compatibilidade</p>
          {[
            { label:'Área de atuação', val: vaga.score_area },
            { label:'Localização',     val: vaga.score_geo  },
          ].filter(i => i.val !== undefined).map(i => (
            <div key={i.label} className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-28 flex-shrink-0">{i.label}</span>
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${(i.val||0)>=70?'bg-green-500':(i.val||0)>=50?'bg-yellow-400':'bg-gray-300'}`}
                  style={{ width:`${i.val||0}%` }}/>
              </div>
              <span className="text-xs font-bold text-gray-500 w-8 text-right">{i.val||0}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Botões */}
      <div className="px-5 pb-6 flex gap-3">
        <button onClick={onPular} disabled={loading}
          className="flex-1 py-3.5 border-2 border-red-200 text-red-400 hover:bg-red-50 hover:border-red-400 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-40">
          <X size={20}/> Pular
        </button>
        <button onClick={onCurtir} disabled={loading}
          className="flex-1 py-3.5 bg-gradient-to-r from-acre-green to-acre-teal text-white rounded-2xl font-bold transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-40 shadow-md shadow-acre-green/30">
          <Heart size={20}/> Interesse
        </button>
      </div>
    </div>
  );
}

// ── Cartão de Candidato (para empresa) ───────────────────────
function CardCandidato({ candidato, vaga, onCurtir, onPular, loading }) {
  const score = candidato.score_total || 0;
  const corScore = score >= 70 ? 'bg-green-500' : score >= 50 ? 'bg-yellow-500' : 'bg-gray-400';
  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden w-full max-w-sm">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#1a3820] to-acre-dark p-6 text-white">
        <div className="flex items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-acre-green to-acre-teal flex items-center justify-center text-white font-bold text-xl shadow-lg flex-shrink-0">
            {candidato.nome?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-sora font-bold text-base truncate">{candidato.nome}</p>
              {candidato.selo_verificado && <Star size={14} className="text-acre-gold flex-shrink-0"/>}
            </div>
            <p className="text-white/60 text-sm">{candidato.cidade || 'Acre'}</p>
          </div>
          <div className={`w-12 h-12 rounded-full ${corScore} flex items-center justify-center font-sora font-black text-white text-sm shadow`}>
            {score}%
          </div>
        </div>
      </div>

      {/* Detalhes */}
      <div className="p-5 space-y-3">
        <div className="flex flex-wrap gap-2">
          {candidato.nivel_escolaridade && (
            <span className="text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">{candidato.nivel_escolaridade}</span>
          )}
          {candidato.experiencia_anos > 0 && (
            <span className="text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-full">{candidato.experiencia_anos}+ anos exp.</span>
          )}
          {candidato.disponibilidade && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full capitalize">{candidato.disponibilidade.replace('_',' ')}</span>
          )}
        </div>

        {candidato.areas_interesse?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {candidato.areas_interesse.slice(0,3).map(a => (
              <span key={a} className="text-xs bg-acre-light text-acre-green border border-acre-green/20 px-2 py-0.5 rounded-full font-medium">{a}</span>
            ))}
          </div>
        )}

        {/* Hard skills */}
        {candidato.hard_skills?.length > 0 && (
          <div>
            <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1"><Brain size={11}/> Hard Skills</p>
            <div className="flex flex-wrap gap-1">
              {(Array.isArray(candidato.hard_skills) ? candidato.hard_skills : []).slice(0,4).map(s => (
                <span key={s} className="text-xs bg-blue-50 text-blue-600 border border-blue-100 px-2 py-0.5 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}

        {/* Score breakdown */}
        <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
          <p className="text-xs font-semibold text-gray-500 mb-2">Compatibilidade com a vaga</p>
          {[
            { label:'Área',        val: candidato.score_area },
            { label:'Localização', val: candidato.score_geo  },
          ].filter(i => i.val !== undefined).map(i => (
            <div key={i.label} className="flex items-center gap-2">
              <span className="text-xs text-gray-400 w-20 flex-shrink-0">{i.label}</span>
              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${(i.val||0)>=70?'bg-green-500':(i.val||0)>=50?'bg-yellow-400':'bg-gray-300'}`}
                  style={{ width:`${i.val||0}%` }}/>
              </div>
              <span className="text-xs font-bold text-gray-500 w-8 text-right">{i.val||0}%</span>
            </div>
          ))}
        </div>

        {vaga && <p className="text-xs text-gray-400 text-center">Para a vaga: <strong className="text-acre-dark">{vaga.titulo}</strong></p>}
      </div>

      {/* Botões */}
      <div className="px-5 pb-6 flex gap-3">
        <button onClick={onPular} disabled={loading}
          className="flex-1 py-3.5 border-2 border-red-200 text-red-400 hover:bg-red-50 hover:border-red-400 rounded-2xl font-bold transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-40">
          <X size={20}/> Passar
        </button>
        <button onClick={onCurtir} disabled={loading}
          className="flex-1 py-3.5 bg-gradient-to-r from-acre-green to-acre-teal text-white rounded-2xl font-bold transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-40 shadow-md shadow-acre-green/30">
          <Heart size={20}/> Curtir
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
export default function MatchTinder() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const tipo     = user?.tipo;

  // Estado
  const [items,    setItems]    = useState([]);
  const [idx,      setIdx]      = useState(0);
  const [loading,  setLoading]  = useState(true);
  const [acao,     setAcao]     = useState(null);
  const [concluido,setConcluido]= useState(false);
  const [vagaSel,  setVagaSel]  = useState(null); // empresa: vaga selecionada
  const [vagas,    setVagas]    = useState([]);    // empresa: lista de vagas
  const [curtidas, setCurtidas] = useState({});

  // ── Carregar vagas da empresa para escolher ───────────────
  const carregarVagasEmpresa = useCallback(async () => {
    try {
      const { data } = await api.get('/vagas/empresa/minhas');
      const abertas = (data || []).filter(v => v.status === 'aberta');
      setVagas(abertas);
      if (abertas.length > 0) setVagaSel(abertas[0]);
    } catch {}
    setLoading(false);
  }, []);

  // ── Carregar matches ──────────────────────────────────────
  const carregar = useCallback(async () => {
    setLoading(true);
    setIdx(0);
    setConcluido(false);
    try {
      if (tipo === 'candidato') {
        const [rMatch, rCurtidas] = await Promise.all([
          api.get('/candidatos/matches'),
          api.get('/candidatos/vagas/curtidas').catch(() => ({ data: {} })),
        ]);
        const todos = rMatch.data.matches || [];
        const curt  = rCurtidas.data || {};
        // Mostrar apenas pendentes (não curtidas/puladas ainda)
        const pendentes = todos.filter(m => !curt[m.vaga_id]);
        setItems(pendentes);
        setCurtidas(curt);
      } else if (tipo === 'empresa' && vagaSel) {
        const { data } = await api.get(`/vagas/${vagaSel.id}/matches?minScore=0&limit=50`);
        setItems(data.matches || []);
      }
    } catch {}
    setLoading(false);
  }, [tipo, vagaSel]);

  useEffect(() => {
    if (tipo === 'empresa') carregarVagasEmpresa();
    else carregar();
  }, [tipo]);

  useEffect(() => {
    if (vagaSel) carregar();
  }, [vagaSel]);

  // ── Ações ─────────────────────────────────────────────────
  const agir = async (acao_key) => {
    const item = items[idx];
    if (!item) return;
    setAcao(acao_key);

    try {
      if (tipo === 'candidato') {
        await api.post(`/candidatos/vagas/${item.vaga_id}/${acao_key === 'curtir' ? 'curtir' : 'pular'}`);
      } else {
        await api.post(`/vagas/${vagaSel.id}/match/candidatos/${item.user_id}/${acao_key}`);
      }
    } catch {}

    setTimeout(() => {
      setAcao(null);
      const proximo = idx + 1;
      if (proximo >= items.length) setConcluido(true);
      else setIdx(proximo);
    }, 300);
  };

  const item   = items[idx];
  const progresso = items.length > 0 ? Math.round((idx / items.length) * 100) : 0;

  // ── UI ───────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1f12] via-[#0f2318] to-[#1a3820] flex flex-col">

      {/* Header */}
      <div className="px-4 pt-5 pb-3 flex items-center justify-between max-w-sm mx-auto w-full">
        <button onClick={() => navigate(tipo==='candidato'?'/candidato':'/empresa')}
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors">
          <ArrowLeft size={16}/> Voltar
        </button>
        <div className="flex items-center gap-2">
          <Zap size={16} className="text-acre-gold"/>
          <span className="text-white font-sora font-bold text-sm">Match</span>
        </div>
        <button onClick={carregar} className="text-white/40 hover:text-white transition-colors">
          <RefreshCw size={15}/>
        </button>
      </div>

      {/* Seletor de vaga (só para empresa) */}
      {tipo === 'empresa' && vagas.length > 1 && (
        <div className="px-4 pb-3 max-w-sm mx-auto w-full">
          <select
            value={vagaSel?.id || ''}
            onChange={e => setVagaSel(vagas.find(v => v.id === e.target.value))}
            className="w-full bg-white/10 text-white text-sm border border-white/20 rounded-xl px-3 py-2 focus:outline-none">
            {vagas.map(v => <option key={v.id} value={v.id} className="text-black">{v.titulo}</option>)}
          </select>
        </div>
      )}

      {/* Barra de progresso */}
      {!loading && items.length > 0 && !concluido && (
        <div className="px-4 pb-3 max-w-sm mx-auto w-full">
          <div className="flex items-center justify-between text-xs text-white/40 mb-1">
            <span>{idx+1} de {items.length}</span>
            <span>{progresso}%</span>
          </div>
          <div className="h-1 bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-acre-gold rounded-full transition-all duration-500" style={{ width:`${progresso}%` }}/>
          </div>
        </div>
      )}

      {/* Conteúdo principal */}
      <div className="flex-1 flex items-center justify-center px-4 pb-8">
        {loading ? (
          <div className="text-center text-white/60">
            <Loader2 size={32} className="animate-spin mx-auto mb-3 text-acre-green"/>
            <p className="text-sm">Calculando compatibilidade...</p>
          </div>
        ) : tipo === 'empresa' && vagas.length === 0 ? (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center">
            <Briefcase size={40} className="text-white/20 mx-auto mb-4"/>
            <h3 className="font-sora font-bold text-white text-lg mb-2">Nenhuma vaga aberta</h3>
            <p className="text-white/50 text-sm mb-5">Publique uma vaga para ver os candidatos compatíveis.</p>
            <button onClick={() => navigate('/empresa/vagas/nova')}
              className="btn-primary text-sm px-5 py-2.5 flex items-center gap-2 mx-auto">
              + Publicar Vaga
            </button>
          </div>
        ) : concluido ? (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-acre-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart size={28} className="text-acre-gold"/>
            </div>
            <h3 className="font-sora font-bold text-white text-xl mb-2">Todos avaliados! 🎉</h3>
            <p className="text-white/60 text-sm mb-6">
              {tipo === 'candidato'
                ? 'Você avaliou todas as vagas disponíveis.'
                : `Você avaliou todos os candidatos para "${vagaSel?.titulo}".`}
            </p>
            <div className="flex flex-col gap-2">
              <button onClick={carregar}
                className="w-full py-3 bg-white/10 hover:bg-white/15 text-white rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                <RefreshCw size={15}/> Ver novamente
              </button>
              <button onClick={() => navigate(tipo==='candidato'?'/candidato/matches':'/empresa/selecao')}
                className="w-full py-3 bg-acre-green hover:bg-acre-mid text-white rounded-2xl font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
                {tipo==='candidato' ? 'Ver vagas compatíveis' : 'Ver selecionados'}
                <ChevronRight size={15}/>
              </button>
            </div>
          </div>
        ) : !item ? (
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 max-w-sm w-full text-center">
            <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-4">
              <X size={28} className="text-white/30"/>
            </div>
            <h3 className="font-sora font-bold text-white text-lg mb-2">
              {tipo === 'candidato' ? 'Nenhuma vaga disponível' : 'Nenhum candidato encontrado'}
            </h3>
            <p className="text-white/50 text-sm mb-4">
              {tipo === 'candidato'
                ? 'Complete seu perfil com áreas de interesse para aparecer mais vagas.'
                : 'Os candidatos aparecem conforme se cadastram e criam perfis.'}
            </p>
            <button onClick={() => navigate(tipo==='candidato'?'/candidato/perfil':'/empresa/vagas')}
              className="text-acre-gold text-sm hover:underline">
              {tipo==='candidato' ? 'Completar perfil →' : 'Gerenciar vagas →'}
            </button>
          </div>
        ) : (
          <div className={`w-full max-w-sm transition-all duration-300 ${acao ? (acao==='curtir'?'translate-x-8 rotate-3':'−translate-x-8 −rotate-3') : ''}`}>
            {tipo === 'candidato' ? (
              <CardVaga
                vaga={item}
                onCurtir={() => agir('curtir')}
                onPular={() => agir('pular')}
                loading={!!acao}
              />
            ) : (
              <CardCandidato
                candidato={item}
                vaga={vagaSel}
                onCurtir={() => agir('curtir')}
                onPular={() => agir('pular')}
                loading={!!acao}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
}
